import { useState } from "react";
import {
  Activity,
  Droplets,
  HeartPulse,
  Inbox,
  Moon,
  Stethoscope,
  Sun,
  Thermometer,
  Wind,
} from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Badge, UrgencyBadge } from "@/components/ui/Badge";
import { Input, Select, Textarea } from "@/components/ui/Field";
import { Alert, EmptyState, Skeleton, UrgencyPanel } from "@/components/ui/Feedback";
import { StatTile } from "@/components/ui/StatTile";
import { useToast } from "@/components/ui/toast";
import { useTheme } from "@/lib/theme";
import { URGENCY_LEVELS, URGENCY_META } from "@/lib/urgency";

/**
 * Living reference for the Mediora design system.
 *
 * It imports the same components the product uses, so it cannot drift from
 * what actually ships — if a token changes, this page changes with it.
 */
export function DesignPreview() {
  const { theme, toggle } = useTheme();
  const toast = useToast();

  return (
    <div className="min-h-dvh">
      <header className="border-hairline bg-surface/85 sticky top-0 z-20 border-b backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-5xl items-center gap-3 px-5">
          <span
            aria-hidden
            className="bg-accent text-accent-content font-display grid size-8 place-items-center text-base font-extrabold"
            style={{ borderRadius: "0.7rem 0.2rem 0.7rem 0.2rem" }}
          >
            M
          </span>
          <div className="flex-1">
            <p className="font-display leading-none font-extrabold tracking-tight">
              Design System
            </p>
            <p className="text-content-subtle text-2xs">Mediora · v2</p>
          </div>
          <Button
            size="sm"
            variant="secondary"
            onClick={toggle}
            icon={theme === "dark" ? <Sun className="size-4" /> : <Moon className="size-4" />}
          >
            {theme === "dark" ? "Terang" : "Gelap"}
          </Button>
        </div>
      </header>

      <div className="mx-auto max-w-5xl space-y-12 px-5 py-10">
        <section>
          <p className="eyebrow mb-2">Arah desain</p>
          <h1 className="max-w-2xl text-4xl">Hitam, putih, dan satu garis lime.</h1>
          <p className="text-content-muted mt-3 max-w-2xl">
            Tiga warna menopang seluruh produk, diambil dari Vesselman: hitam{" "}
            <code className="font-mono text-sm">oklch(13.5% .01 285)</code>, putih{" "}
            <code className="font-mono text-sm">oklch(95% .006 95)</code>, dan lime{" "}
            <code className="font-mono text-sm">oklch(88% .212 122)</code>. Selebihnya hanyalah
            langkah pada ramp yang sama. Warna hanya dipakai kalau punya arti — lime menandai aksi
            utama, skala klinis menandai penyimpangan dari normal. Tidak ada warna dekoratif.
          </p>
        </section>

        <Section title="Warna" caption="Satu sumbu hitam-putih, satu aksen">
          <Ramp
            name="Void — permukaan, garis, dan teks"
            steps={["950", "900", "850", "800", "700", "600", "500", "400", "300", "200", "100", "50"]}
            prefix="void"
          />
          <Ramp
            name="Flux — aksi utama & fokus"
            steps={["100", "200", "300", "400", "500", "600", "700", "800", "900"]}
            prefix="flux"
          />
        </Section>

        <Section
          title="Urgensi klinis"
          caption="Satu skala untuk triase, cek kesehatan, dan alert"
        >
          <Alert tone="warning" title="Kenapa ini penting">
            Backend memakai tiga kosakata untuk hal yang sama:{" "}
            <code className="font-mono text-xs">green/yellow/orange/red</code>,{" "}
            <code className="font-mono text-xs">MERAH/KUNING/HIJAU</code>, dan{" "}
            <code className="font-mono text-xs">monitor/urgent/emergency</code>. Semuanya
            dinormalisasi di satu tempat, jadi merah di kartu alert dan merah di badge triase tidak
            bisa lagi berbeda arti.
          </Alert>

          <Alert tone="info" className="mt-3" title="Kenapa &quot;Aman&quot; bukan hijau">
            Aksen produk ini lime-hijau. Kalau hasil aman juga hijau, pasien bisa membaca badge
            status sebagai tombol aksi. Karena itu tingkat “Aman” memakai teal — cukup jauh dari
            lime untuk tidak pernah tertukar.
          </Alert>

          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {URGENCY_LEVELS.map((level) => (
              <div
                key={level}
                className="border-hairline bg-surface flex items-start gap-3 rounded-xl border p-4"
              >
                <span className={`mt-1 size-2.5 shrink-0 rounded-full ${URGENCY_META[level].dot}`} />
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <UrgencyBadge level={level} />
                    <code className="text-content-subtle font-mono text-2xs">{level}</code>
                  </div>
                  <p className="text-content-muted mt-1.5 text-sm">
                    {URGENCY_META[level].guidance}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 space-y-3">
            <UrgencyPanel level="critical" title="Tanda bahaya terdeteksi">
              Nyeri dada disertai sesak napas memerlukan penanganan segera. Hubungi{" "}
              <b>119</b> atau kunjungi IGD terdekat. Jangan menunggu balasan chatbot.
            </UrgencyPanel>
            <UrgencyPanel level="monitor" title="Tekanan darah sedikit di atas normal">
              Ulangi pengukuran dalam kondisi istirahat 3 hari berturut-turut, lalu bawa hasilnya
              saat konsultasi.
            </UrgencyPanel>
          </div>
        </Section>

        <Section title="Tipografi" caption="Manrope display · DM Sans teks">
          <div className="border-hairline bg-surface space-y-4 rounded-xl border p-5">
            {[
              ["text-4xl", "Judul halaman utama", "42px"],
              ["text-3xl", "Judul bagian besar", "33px"],
              ["text-2xl", "Judul halaman", "26px"],
              ["text-xl", "Judul kartu", "21px"],
              ["text-base", "Teks isi — ukuran baca utama", "15px"],
              ["text-sm", "Teks pendukung dan tabel", "13px"],
              ["text-xs", "Keterangan kecil", "12px"],
            ].map(([cls, label, size]) => (
              <div key={cls} className="flex items-baseline gap-4 border-b border-dashed pb-3 last:border-0 last:pb-0">
                <code className="text-content-subtle w-24 shrink-0 font-mono text-2xs">{size}</code>
                <span className={`${cls} ${cls.includes("4xl") || cls.includes("3xl") || cls.includes("2xl") || cls.includes("xl") ? "font-display font-bold" : ""}`}>
                  {label}
                </span>
              </div>
            ))}
            <div className="flex items-baseline gap-4 pt-1">
              <code className="text-content-subtle w-24 shrink-0 font-mono text-2xs">11px</code>
              <span className="eyebrow">Label bagian</span>
            </div>
          </div>
        </Section>

        <Section title="Tombol" caption="Satu aksi utama per layar">
          <div className="border-hairline bg-surface space-y-4 rounded-xl border p-5">
            <div className="flex flex-wrap items-center gap-2.5">
              <Button variant="primary" icon={<HeartPulse className="size-4" />}>
                Mulai pemeriksaan
              </Button>
              <Button variant="secondary">Sekunder</Button>
              <Button variant="dark">Gelap</Button>
              <Button variant="ghost">Ghost</Button>
              <Button variant="danger">Hapus</Button>
            </div>
            <div className="flex flex-wrap items-center gap-2.5">
              <Button size="sm">Kecil</Button>
              <Button size="md">Sedang</Button>
              <Button size="lg" variant="primary">
                Besar
              </Button>
              <Button loading>Memproses</Button>
              <Button disabled>Nonaktif</Button>
            </div>
          </div>
        </Section>

        <Section title="Metrik" caption="Label kecil, angka besar — kontras ukuran">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatTile
              label="Denyut jantung"
              value="72"
              unit="bpm"
              trend="flat"
              trendValue="0%"
              caption="vs baseline"
              icon={<HeartPulse className="size-4" />}
            />
            <StatTile
              label="Saturasi oksigen"
              value="98"
              unit="%"
              trend="up"
              trendValue="1%"
              caption="7 hari"
              icon={<Wind className="size-4" />}
            />
            <StatTile
              label="Tekanan darah"
              value="128/84"
              trend="down"
              trendValue="4%"
              caption="7 hari"
              icon={<Activity className="size-4" />}
            />
            <StatTile
              label="Suhu tubuh"
              value="36.8"
              unit="°C"
              caption="alat digital"
              icon={<Thermometer className="size-4" />}
            />
          </div>
        </Section>

        <Section title="Grafik" caption="Recharts memakai token, ikut tema">
          <Card>
            <CardHeader
              eyebrow="30 hari terakhir"
              title="Tren denyut jantung istirahat"
              action={<Badge tone="routine" dot>Stabil</Badge>}
            />
            <CardBody>
              <TrendChart />
            </CardBody>
          </Card>
        </Section>

        <Section title="Formulir" caption="Hint dan error tidak pernah menumpuk">
          <Card>
            <CardBody className="grid gap-4 sm:grid-cols-2">
              <Input
                label="Nomor rekam medis"
                placeholder="RM-000123"
                hint="Tertera pada kartu pasien Anda."
                required
              />
              <Input
                label="Tekanan sistolik"
                type="number"
                placeholder="120"
                error="Nilai harus antara 60 dan 260 mmHg."
              />
              <Select label="Area pemeriksaan" defaultValue="">
                <option value="" disabled>
                  Pilih area
                </option>
                <option>Kepala</option>
                <option>Tulang belakang</option>
                <option>Lutut</option>
              </Select>
              <Input label="Tanggal pemeriksaan" type="date" />
              <Textarea
                className="sm:col-span-2"
                label="Konteks klinis"
                placeholder="Jelaskan keluhan tanpa menyertakan data identitas."
                hint="Jangan tulis nama, NIK, atau nomor telepon."
              />
            </CardBody>
          </Card>
        </Section>

        <Section title="Umpan balik" caption="Pengganti flash() server-side">
          <div className="space-y-3">
            <Alert tone="success" title="Pemeriksaan tersimpan">
              Hasil sudah masuk ke riwayat kesehatan Anda.
            </Alert>
            <Alert tone="info">Kredit AI Anda tersisa 12 dari 20 hari ini.</Alert>
            <Alert tone="danger" title="Kuota tidak cukup">
              MRI Assist memerlukan 5 kredit. Perbarui langganan untuk melanjutkan.
            </Alert>

            <div className="flex flex-wrap gap-2.5 pt-1">
              <Button
                size="sm"
                onClick={() =>
                  toast.success({
                    title: "Pemeriksaan tersimpan",
                    description: "Hasil masuk ke riwayat kesehatan Anda.",
                  })
                }
              >
                Toast sukses
              </Button>
              <Button
                size="sm"
                onClick={() =>
                  toast.warning({
                    title: "Koneksi lambat",
                    description: "Analisis mungkin memerlukan waktu lebih lama.",
                  })
                }
              >
                Toast peringatan
              </Button>
              <Button
                size="sm"
                onClick={() =>
                  toast.danger({
                    title: "Gagal menyimpan",
                    description: "Periksa koneksi Anda, lalu coba lagi.",
                  })
                }
              >
                Toast error
              </Button>
            </div>
          </div>
        </Section>

        <Section title="Status kosong & memuat">
          <div className="grid gap-3 sm:grid-cols-2">
            <Card>
              <EmptyState
                icon={<Inbox className="size-5" />}
                title="Belum ada pemeriksaan"
                description="Riwayat akan muncul di sini setelah pemeriksaan pertama Anda."
                action={
                  <Button size="sm" variant="primary" icon={<Stethoscope className="size-4" />}>
                    Mulai sekarang
                  </Button>
                }
              />
            </Card>
            <Card>
              <CardBody className="space-y-3">
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-4 w-1/2" />
              </CardBody>
            </Card>
          </div>
        </Section>

        <Section title="Label">
          <div className="border-hairline bg-surface flex flex-wrap gap-2 rounded-xl border p-5">
            <Badge>Netral</Badge>
            <Badge tone="accent">12 kredit</Badge>
            <Badge tone="info" dot>
              Terhubung
            </Badge>
            {URGENCY_LEVELS.map((level) => (
              <UrgencyBadge key={level} level={level} />
            ))}
          </div>
        </Section>

        <footer className="border-hairline text-content-subtle border-t pt-6 pb-4 text-sm">
          <div className="flex items-center gap-2">
            <Droplets className="size-4" aria-hidden />
            <span>
              Komponen di halaman ini adalah komponen yang dipakai produk — bukan salinan.
            </span>
          </div>
        </footer>
      </div>
    </div>
  );
}

function Section({
  title,
  caption,
  children,
}: {
  title: string;
  caption?: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-4">
        <h2 className="text-xl">{title}</h2>
        {caption && <p className="text-content-subtle mt-0.5 text-sm">{caption}</p>}
      </div>
      {children}
    </section>
  );
}

function Ramp({
  name,
  steps,
  prefix,
}: {
  name: string;
  steps: string[];
  prefix: string;
}) {
  return (
    <div className="mb-5">
      <p className="text-content-muted mb-1.5 text-sm font-semibold">{name}</p>
      <div className="border-hairline flex overflow-hidden rounded-xl border">
        {steps.map((step) => (
          <div key={step} className="flex-1">
            {/* Inline style, not `bg-${prefix}-${step}` — Tailwind only emits
                classes it can see as complete literals in the source. */}
            <div className="h-12" style={{ background: `var(--color-${prefix}-${step})` }} />
            <div className="bg-surface text-content-subtle py-1 text-center font-mono text-2xs">
              {step}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const sampleTrend = [
  { day: "1", value: 74 },
  { day: "5", value: 72 },
  { day: "10", value: 76 },
  { day: "15", value: 71 },
  { day: "20", value: 73 },
  { day: "25", value: 70 },
  { day: "30", value: 72 },
];

function TrendChart() {
  const [active, setActive] = useState<number | null>(null);
  return (
    <div className="h-56 w-full" onMouseLeave={() => setActive(null)}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={sampleTrend} margin={{ top: 8, right: 8, left: -24, bottom: 0 }}>
          <defs>
            <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--color-flux-400)" stopOpacity={0.35} />
              <stop offset="100%" stopColor="var(--color-flux-400)" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 5" vertical={false} stroke="var(--hairline)" />
          <XAxis
            dataKey="day"
            axisLine={false}
            tickLine={false}
            tick={{ fill: "var(--content-subtle)", fontSize: 11 }}
            dy={6}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            domain={[60, 85]}
            tick={{ fill: "var(--content-subtle)", fontSize: 11 }}
          />
          <Tooltip
            cursor={{ stroke: "var(--hairline-strong)", strokeWidth: 1 }}
            contentStyle={{
              background: "var(--surface)",
              border: "1px solid var(--hairline-strong)",
              borderRadius: "0.75rem",
              fontSize: "13px",
              boxShadow: "var(--shadow-float)",
              color: "var(--content)",
            }}
            labelFormatter={(day) => `Hari ke-${day}`}
            formatter={(value) => [`${value} bpm`, "Denyut istirahat"]}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke="var(--color-flux-600)"
            strokeWidth={2}
            fill="url(#trendFill)"
            activeDot={{ r: 4, fill: "var(--color-flux-600)", stroke: "var(--surface)", strokeWidth: 2 }}
            onMouseEnter={() => setActive(1)}
            isAnimationActive={active === null}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
